import React, { useState } from 'react';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalNavigate, usePortalPath } from '@/hooks/usePortalPath';
import { useTenantId } from '@/contexts/TenantContext';
import ClientPortalDisabledScreen from '@/components/auth/ClientPortalDisabledScreen';
import { CLIENT_PORTAL_DISABLED_MESSAGE } from '@core/portal/clientPortal.util';
import {
  ERROR_CODE_INCORRECT_APP_CONTEXT,
  findErrorByCode,
} from '@core/util/graphqlError.util';
import {
  actionAuthSubmitButtonClassName,
  actionFormInputClassName,
  actionLinkClassName,
} from '@core/theme/actionTheme.util';
import { AuthHeader } from '@/components/auth/AuthHeader';

const ConfirmSignUp = () => {
  const location = useLocation();
  const portalNavigate = usePortalNavigate();
  const { path: portalPath } = usePortalPath();
  const { checkAuth, signIn } = useAuth();
  const tenantId = useTenantId();
  const email: string = location.state?.email;
  const [formState, setFormState] = useState({
    code: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isClientPortalDisabled, setIsClientPortalDisabled] = useState(false);

  const isClientPortalDisabledError = (signInError: unknown): boolean => {
    if (
      signInError instanceof Error &&
      signInError.message === CLIENT_PORTAL_DISABLED_MESSAGE
    ) {
      return true;
    }

    return Boolean(
      findErrorByCode(signInError, ERROR_CODE_INCORRECT_APP_CONTEXT),
    );
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (!email) {
        setError('Email is required');
        return;
      }

      if (!tenantId) {
        setError('Tenant ID is required');
        return;
      }

      // Construct Cognito username as tenantId#email
      const cognitoUsername = `${tenantId}#${email}`;

      const confirmSignUpOutput = await confirmSignUp({
        username: cognitoUsername,
        confirmationCode: formState.code,
      });

      // Handle successful confirmation
      if (confirmSignUpOutput.isSignUpComplete) {
        try {
          // Sign in the user with their credentials using the auth context
          await signIn({
            email,
            password: location.state?.password || '',
          });
          // Navigate to dashboard after successful sign-in
          await checkAuth(portalPath('/onboarding'));
        } catch (signInError) {
          if (isClientPortalDisabledError(signInError)) {
            setIsClientPortalDisabled(true);
            return;
          }

          console.error('Sign-in failed:', signInError);
          portalNavigate('/login', {
            state: {
              message: 'Please sign in with your credentials',
            },
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestNewCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      if (!email) {
        setError('Email is required');
        return;
      }

      if (!tenantId) {
        setError('Tenant ID is required');
        return;
      }

      // Construct Cognito username as tenantId#email
      const cognitoUsername = `${tenantId}#${email}`;

      await resendSignUpCode({
        username: cognitoUsername,
      });
      setError('A new confirmation code has been sent to your email');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to send new confirmation code',
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isClientPortalDisabled) {
    return <ClientPortalDisabledScreen />;
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <div className="w-full max-w-md px-6 py-8">
        <AuthHeader title="Confirm Sign Up" />

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700"
              >
                Confirmation Code
              </label>
              <p className="block text-sm font-medium text-gray-400">
                Check your email for confirmation code
              </p>
              <input
                id="code"
                name="code"
                type="text"
                required
                className={actionFormInputClassName}
                placeholder="Enter confirmation code"
                value={formState.code}
                onChange={(e) =>
                  setFormState({ ...formState, code: e.target.value })
                }
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`${actionAuthSubmitButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isLoading ? 'Confirming sign up...' : 'Confirm Sign Up'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button
            onClick={handleRequestNewCode}
            className={`font-medium ${actionLinkClassName}`}
          >
            Request a new code
          </button>
        </div>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <a href="/login" className={`font-medium ${actionLinkClassName}`}>
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSignUp;
